/**
 * The sentences a table card shows for its table changes, one per change,
 * under the table `PreviewPackage` composes: which row a new row follows and
 * what its cells say, which row goes, and what table appears where.
 *
 * The card names rows by the text of the cell the operation anchored on, so the
 * user reads `the row containing "Premium support"` and never a bookmark name.
 * The caller says where that text comes from: the live document for the live
 * preview, the card's own paragraphs for the sentence the card shows before
 * Word has been read.
 */
import type { TableOperationsApplyResult } from "@common/utils/redline/table-operations";

/** How many characters of an anchor cell's text a sentence quotes before eliding. */
const ANCHOR_TEXT_LIMIT = 40;

export interface TableChangePreviewRequest {
    edits: TableOperationsApplyResult;
    /** The accepted text of the paragraph a bookmark names, or null when the document no longer holds it. */
    anchorTextOf: (bookmark: string) => string | null;
}

/**
 * The three ways an anchor reads in a sentence.
 *
 * CASES
 *   - "quoted"  -> the anchor has text: it is quoted, cut to the limit
 *   - "blank"   -> the anchor paragraph holds only whitespace
 *   - "missing" -> the document (or the card) no longer holds the paragraph
 */
type AnchorReading =
    | { kind: "quoted"; quotedText: string }
    | { kind: "blank" }
    | { kind: "missing" };

export class TableChangePreview {
    /**
     * EXAMPLE
     *   one row deleted, two rows inserted after the "Premium support" row, two tables added
     *   -> [ 'Deletes the row containing "Platform".',
     *        'Adds a row after the row containing "Premium support", with the cells "Annual uplift cap", "percentage of prior-year fee" and "3%".',
     *        'Adds another row after it, with the cells "Onboarding", "one-off" and "$5,000".',
     *        'Adds a table of 2 rows and 3 columns after "Schedule 1 - Fees and Payment Terms for …".',
     *        'Adds a table of 1 row and 1 column after the table containing "Premium support".' ]
     */
    static lines(request: TableChangePreviewRequest): string[] {
        const lines: string[] = [];
        for (const deletion of request.edits.rowDeletions) {
            lines.push(`Deletes ${TableChangePreview.rowNamed(TableChangePreview.anchorReadingOf(request, deletion.rowOfBookmark))}.`);
        }
        for (const insertion of request.edits.rowInsertions) {
            const anchorRow = TableChangePreview.rowNamed(TableChangePreview.anchorReadingOf(request, insertion.afterRowOfBookmark));
            insertion.rows.forEach((cellTexts, rowPosition) => {
                /**
                 * CASES
                 *   - the insertion's first row (position 0) -> it follows the anchor row, which the sentence names
                 *   - every later row                        -> it follows the row the sentence before added
                 *
                 * EXAMPLE
                 *   rows = [["Annual uplift cap", "3%"], ["Onboarding", "$5,000"]]
                 *   rowPosition 0 -> 'Adds a row after the row containing "Premium support", with the cells …'
                 *   rowPosition 1 -> 'Adds another row after it, with the cells …'
                 */
                const opening = rowPosition === 0 ? `Adds a row after ${anchorRow}` : "Adds another row after it";
                lines.push(`${opening}, with ${TableChangePreview.cellsListed(cellTexts)}.`);
            });
        }
        for (const tableInsertion of request.edits.tableInsertions) {
            /**
             * EXAMPLE
             *   rows = [["Component", "Unit", "Fee"], ["Platform", "per year", "$120,000"]] -> "2 rows and 3 columns"
             *   rows = [["Only"]]                                                          -> "1 row and 1 column"
             *   rows = []                                                                  -> "0 rows and 0 columns" (rows[0] is undefined)
             */
            const rowCount = tableInsertion.rows.length;
            const columnCount = tableInsertion.rows[0]?.length ?? 0;
            const size = `${rowCount} ${rowCount === 1 ? "row" : "rows"} and ${columnCount} ${columnCount === 1 ? "column" : "columns"}`;
            const anchor = TableChangePreview.anchorReadingOf(request, tableInsertion.afterBookmark);
            /**
             * CASES
             *   - "after_paragraph" -> the table goes right after that paragraph, inside its cell when it is one
             *   - "after_table"     -> the table goes after the whole table that holds the anchor
             */
            const where = tableInsertion.place === "after_table" ? TableChangePreview.tableNamed(anchor) : TableChangePreview.paragraphNamed(anchor);
            lines.push(`Adds a table of ${size} after ${where}.`);
        }
        return lines;
    }

    /**
     * CASES
     *   - the anchor paragraph has text           -> "quoted", its trimmed text cut to the limit
     *   - it holds only whitespace                -> "blank"
     *   - the caller's source no longer holds it  -> "missing"
     *
     * EXAMPLE
     *   limit 40, text "Schedule 1 - Fees and Payment Terms for the Platform Services"
     *   slice(0, 40) -> "Schedule 1 - Fees and Payment Terms for " -> quoted as "Schedule 1 - Fees and Payment Terms for …"
     */
    private static anchorReadingOf(request: TableChangePreviewRequest, bookmark: string): AnchorReading {
        const text = request.anchorTextOf(bookmark);
        if (text === null) return { kind: "missing" };
        const trimmed = text.trim();
        if (trimmed.length === 0) return { kind: "blank" };
        return { kind: "quoted", quotedText: `"${trimmed.length <= ANCHOR_TEXT_LIMIT ? trimmed : `${trimmed.slice(0, ANCHOR_TEXT_LIMIT)}…`}"` };
    }

    /**
     * EXAMPLE
     *   quoted "Platform" -> 'the row containing "Platform"'
     *   blank             -> "the row with an empty cell"
     *   missing           -> "a row that is no longer in the document"
     */
    private static rowNamed(anchor: AnchorReading): string {
        if (anchor.kind === "quoted") return `the row containing ${anchor.quotedText}`;
        if (anchor.kind === "blank") return "the row with an empty cell";
        return "a row that is no longer in the document";
    }

    /**
     * EXAMPLE
     *   quoted "Premium support" -> 'the table containing "Premium support"'
     *   blank                    -> "the table with an empty cell"
     *   missing                  -> "a table that is no longer in the document"
     */
    private static tableNamed(anchor: AnchorReading): string {
        if (anchor.kind === "quoted") return `the table containing ${anchor.quotedText}`;
        if (anchor.kind === "blank") return "the table with an empty cell";
        return "a table that is no longer in the document";
    }

    /**
     * EXAMPLE
     *   quoted "Schedule 1" -> '"Schedule 1"'
     *   blank               -> "an empty paragraph"
     *   missing             -> "a paragraph that is no longer in the document"
     */
    private static paragraphNamed(anchor: AnchorReading): string {
        if (anchor.kind === "quoted") return anchor.quotedText;
        if (anchor.kind === "blank") return "an empty paragraph";
        return "a paragraph that is no longer in the document";
    }

    /**
     * A new row's cells as an English list: each cell quoted, a blank cell named
     * as such, the last two joined by "and".
     *
     * EXAMPLE
     *   ["Layout row", "1", "999"] -> 'the cells "Layout row", "1" and "999"'
     *   ["Audit row", " ", "999"]  -> 'the cells "Audit row", an empty cell and "999"'
     *   ["Only"]                   -> 'the cell "Only"'
     *   [""]                       -> "an empty cell"
     *   []                         -> "no cells"
     */
    private static cellsListed(cellTexts: string[]): string {
        const cellPhrases = cellTexts.map((cellText) => (cellText.trim().length === 0 ? "an empty cell" : `"${cellText.trim()}"`));
        /**
         * CASES
         *   - no cells   -> "no cells"
         *   - one cell   -> "the cell …", or "an empty cell" alone when that one cell is blank
         *   - more cells -> every phrase but the last joined by ", ", then " and " the last
         *
         * EXAMPLE
         *   cellPhrases = ['"a"', '"b"', '"c"'] -> slice(0, -1) = ['"a"', '"b"'] -> '"a", "b"' + ' and "c"'
         */
        if (cellPhrases.length === 0) return "no cells";
        if (cellPhrases.length === 1) return cellPhrases[0] === "an empty cell" ? "an empty cell" : `the cell ${cellPhrases[0]}`;
        return `the cells ${cellPhrases.slice(0, -1).join(", ")} and ${cellPhrases[cellPhrases.length - 1]}`;
    }
}
